/* eslint-disable react/prop-types */
import React, { useState, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  Briefcase,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Wallet,
  FileText,
  User,
  Home,
} from "lucide-react";
import { CareGiverContext } from "../context/CareGiverContext";
import { useTheme } from "../context/ThemeContext";
import { assets } from "../assets/assets";
import LogoutModal from "./LogoutModal";

const Sidebar = ({
  isCollapsed,
  toggleCollapse,
  isMobileOpen,
  closeMobile,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const { setCToken } = useContext(CareGiverContext);
  const { isDarkMode } = useTheme();

  // Caregiver Navigation Items
  const caregiverNavItems = [
    { label: t("sidebar.dashboard"), icon: Home, path: "/caregiver/dashboard" },
    {
      label: t("sidebar.bookings"),
      icon: CalendarDays,
      path: "/caregiver/bookings",
    },
    {
      label: t("sidebar.serviceRequests"),
      icon: Briefcase,
      path: "/caregiver/request/apply",
    },
    {
      label: t("sidebar.applicationStatus"),
      icon: ClipboardCheck,
      path: "/caregiver/application-status",
    },
    {
      label: t("sidebar.contracts"),
      icon: FileText,
      path: "/caregiver/contracts",
    },
    { label: t("sidebar.payments"), icon: Wallet, path: "/caregiver/payments" },
    { label: t("sidebar.profile"), icon: User, path: "/caregiver/profile" },
  ];

  const navItems = caregiverNavItems;

  const linkClass = (isActive) => `
    flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group relative
    ${
      isActive
        ? `${
            isDarkMode ? "bg-primary text-dark-text" : "bg-primary text-white"
          } shadow-lg ${isDarkMode ? "shadow-primary/20" : "shadow-primary/20"}`
        : `${
            isDarkMode
              ? "text-dark-muted hover:bg-dark-background hover:text-primary"
              : "text-text hover:bg-accent hover:text-primary"
          }`
    }
  `;

  const handleLogout = () => {
    setShowLogoutModal(false);
    setCToken(null);
    navigate("/login");
  };

  return (
    <>
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-30
          flex flex-col transition-all duration-300 ease-in-out
          ${isCollapsed ? "w-20" : "w-64"}
          ${
            isMobileOpen
              ? "translate-x-0"
              : "-translate-x-full md:translate-x-0"
          }
          ${
            isDarkMode
              ? "bg-dark-surface border-dark-border"
              : "bg-card border-accent"
          }
          border-r
        `}
      >
        {/* Header */}
        <div
          className={`h-20 flex items-center justify-between px-6 ${
            isDarkMode ? "border-dark-border" : "border-accent"
          } border-b`}
        >
          {!isCollapsed && (
            <div className={`${!isCollapsed ? "block" : "hidden"}`}>
              <img
                className="w-20 cursor-pointer"
                src={isDarkMode ? assets.admin_logo_white : assets.admin_logo}
                alt="Logo"
              />
            </div>
          )}
          {isCollapsed && (
            <div className="w-full flex flex-col items-center">
              <img
                className="w-10 h-10 cursor-pointer mb-1"
                src={isDarkMode ? assets.admin_logo_white : assets.admin_logo}
                alt="Logo"
              />
              <span
                className={`text-xs ${
                  isDarkMode ? "text-dark-muted" : "text-gray-500"
                }`}
              >
                C
              </span>
            </div>
          )}

          <button
            onClick={toggleCollapse}
            className={`hidden md:flex p-1.5 rounded-lg absolute -right-3 top-8 shadow-sm transition-colors ${
              isDarkMode
                ? "bg-dark-background text-dark-muted hover:text-primary border-dark-border"
                : "bg-background text-gray-500 hover:text-primary border-accent"
            } border`}
          >
            {isCollapsed ? (
              <ChevronRight size={14} />
            ) : (
              <ChevronLeft size={14} />
            )}
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-6 px-4 space-y-6 custom-scrollbar">
          {/* Menu Section */}
          <div>
            {!isCollapsed && (
              <p
                className={`text-xs font-semibold uppercase tracking-wider mb-4 px-2 ${
                  isDarkMode ? "text-dark-muted" : "text-gray-400"
                }`}
              >
                {t("sidebar.menu")}
              </p>
            )}
            <div className="space-y-2">
              {navItems.map((item) => (
                <div key={item.label}>
                  <NavLink
                    to={item.path}
                    onClick={closeMobile}
                    className={({ isActive }) => linkClass(isActive)}
                  >
                    <item.icon
                      size={22}
                      className={isCollapsed ? "mx-auto" : ""}
                    />
                    {!isCollapsed && (
                      <span className="font-medium">{item.label}</span>
                    )}

                    {/* Tooltip for collapsed state */}
                    {isCollapsed && (
                      <div
                        className={`absolute left-full ml-4 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none ${
                          isDarkMode
                            ? "bg-dark-background text-dark-text"
                            : "bg-gray-800 text-white"
                        } text-xs`}
                      >
                        {item.label}
                      </div>
                    )}
                  </NavLink>
                </div>
              ))}
            </div>
          </div>

          {/* General Section */}
          <div>
            {!isCollapsed && (
              <p
                className={`text-xs font-semibold uppercase tracking-wider mb-4 px-2 ${
                  isDarkMode ? "text-dark-muted" : "text-gray-400"
                }`}
              >
                {t("sidebar.general")}
              </p>
            )}
            <div className="space-y-2">
              <NavLink
                to="/caregiver/help"
                onClick={closeMobile}
                className={({ isActive }) => linkClass(isActive)}
              >
                <HelpCircle
                  size={22}
                  className={isCollapsed ? "mx-auto" : ""}
                />
                {!isCollapsed && (
                  <span className="font-medium">{t("sidebar.help")}</span>
                )}
              </NavLink>
              <button
                onClick={() => setShowLogoutModal(true)}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 ${
                  isDarkMode
                    ? "text-red-400 hover:bg-red-900/20"
                    : "text-red-600 hover:bg-red-50"
                } ${isCollapsed ? "justify-center" : ""}`}
              >
                <LogOut size={22} className={isCollapsed ? "mx-auto" : ""} />
                {!isCollapsed && (
                  <span className="font-medium">{t("sidebar.logout")}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default Sidebar;
